import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useNavigation } from '@/context/NavigationContext';
import { listTenantActions, type TenantActionRecord } from '@/lib/onboarding';
import { useScope } from '@workspace/scope-context';

function isBlockingAction(action: TenantActionRecord) {
  return action.required && !['completed', 'dismissed', 'expired'].includes(action.status);
}

export default function SetupPage() {
  const navigation = useNavigation();
  const { activeTenant } = useScope();
  const [actions, setActions] = useState<TenantActionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTenant?.id) {
      setActions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listTenantActions(activeTenant.id)
      .then(nextActions => {
        if (!cancelled) {
          setActions(nextActions);
        }
      })
      .catch((fetchError: unknown) => {
        if (!cancelled) {
          setError(fetchError instanceof Error ? fetchError.message : 'Failed to load onboarding tasks.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeTenant?.id]);

  const requiredActions = useMemo(() => actions.filter(isBlockingAction), [actions]);
  const optionalActions = useMemo(
    () => actions.filter(action => !isBlockingAction(action) && action.status !== 'completed'),
    [actions]
  );

  if (!activeTenant?.id) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4 px-6 py-10">
        <h1 className="text-2xl font-semibold text-foreground">Setup requires an active tenant</h1>
        <p className="text-sm text-muted-foreground">
          Select an approved tenant to continue with onboarding.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-10">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Onboarding</p>
        <h1 className="text-3xl font-semibold text-foreground">Finish setup for {activeTenant.name ?? activeTenant.id}</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Complete the required tenant tasks below before moving into the reseller workspace. Optional tasks stay on your dashboard checklist afterward.
        </p>
      </div>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card/60 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading tenant onboarding tasks…
        </div>
      ) : (
        <>
          <section className="rounded-2xl border border-border/60 bg-card/70 p-6 shadow-sm">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-foreground">Required before you continue</h2>
              <p className="text-sm text-muted-foreground">
                These items come directly from the tenant onboarding service and block first-run completion until they move out of a pending state.
              </p>
            </div>

            {requiredActions.length === 0 ? (
              <div className="mt-5 rounded-xl border border-dashed border-emerald-500/30 bg-emerald-500/5 p-4 text-sm text-emerald-800">
                Required setup is complete for this tenant.
              </div>
            ) : (
              <ol className="mt-5 space-y-4">
                {requiredActions.map(action => (
                  <li key={action.actionKey} className="rounded-xl border border-dashed border-border/70 bg-background/70 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="font-medium text-foreground">{action.title}</p>
                        {action.description ? <p className="text-sm text-muted-foreground">{action.description}</p> : null}
                      </div>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                        {action.status.replaceAll('_', ' ')}
                      </span>
                    </div>
                    {action.ctaLabel && action.ctaUrl ? (
                      <div className="mt-4">
                        <Button onClick={() => navigation.navigate(action.ctaUrl!)}>{action.ctaLabel}</Button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {optionalActions.length > 0 ? (
            <section className="rounded-2xl border border-border/60 bg-card/70 p-6 shadow-sm">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">Optional follow-up</h2>
                <p className="text-sm text-muted-foreground">
                  These tasks remain available from the dashboard checklist after onboarding.
                </p>
              </div>
              <ul className="mt-5 space-y-3">
                {optionalActions.map(action => (
                  <li key={action.actionKey} className="rounded-xl border border-dashed border-border/70 bg-background/70 p-4">
                    <p className="font-medium text-foreground">{action.title}</p>
                    {action.description ? <p className="mt-1 text-sm text-muted-foreground">{action.description}</p> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => navigation.navigate('/dashboard')}>
              {requiredActions.length === 0 ? 'Enter workspace' : 'Return to dashboard'}
            </Button>
            <Button variant="outline" onClick={() => navigation.navigate('/settings')}>
              Manage tenant access
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
